import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Flex, Text, Input, InputGroup, InputLeftElement, InputRightElement,
  Select, Spinner, Icon, Button, Table, Thead, Tbody, Tr, Th, Td,
  IconButton, Badge, useToast, Collapse,
} from '@chakra-ui/react';
import { CloseIcon, AddIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import {
  FiSearch, FiRefreshCw, FiTrash2, FiUser, FiEdit2, FiCheck, FiX, FiPlus,
} from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import {
  getEvaluacionesPendientes, createEvaluacionesPendientes,
  updateEvaluacionPendiente, deleteEvaluacionPendiente, buildRow,
} from 'services/evaluacionesPendientesService';
import { searchCustomers } from 'services/unifiedCustomersSearch';
import { normalizeCourseName, codeToDisplayLabel } from 'utils/courseUtils';

// ── helpers ───────────────────────────────────────────────────────────────────

const fmtDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('T')[0].split('-');
  return `${d}/${m}/${y}`;
};

// searchCustomers devuelve { name, group (=group_name raw), username (=RUT) }
const customerCourse = (c) => {
  const raw  = c?.group || c?.meta_data?.find(m => m.key === 'group_name')?.value || '';
  const code = normalizeCourseName(raw);
  return code ? codeToDisplayLabel(code) : raw;
};

const customerName = (c) => c?.name || [c?.first_name, c?.last_name].filter(Boolean).join(' ') || c?.username || '';

const ASIGNATURAS = [
  'Lenguaje y Comunicación', 'Matemática', 'Historia', 'Ciencias Naturales',
  'Inglés', 'Educación Física', 'Artes Visuales', 'Música', 'Tecnología',
  'Orientación', 'Religión', 'Otra',
];

// ── sub-components ─────────────────────────────────────────────────────────────

const Avatar = ({ name = '', size = 32 }) => {
  const COLORS = ['#3A66C8','#C56516','#1E9E63','#6849C4','#C03F76','#147F77'];
  const bg = COLORS[(name.charCodeAt(0) || 0) % COLORS.length];
  const letters = name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
  return (
    <Flex w={`${size}px`} h={`${size}px`} borderRadius="50%" bg={bg} flexShrink={0}
      align="center" justify="center" color="white" fontWeight="700"
      fontSize={`${Math.round(size * 0.36)}px`}>
      {letters || '?'}
    </Flex>
  );
};

const selectSt = {
  bg: 'white', border: '1.5px solid', borderColor: 'cream.line2', borderRadius: '10px',
  fontSize: '13px', color: 'ink.700', fontWeight: '600', size: 'sm',
  _focus: { borderColor: 'coral.500' },
};
const inputSt = {
  bg: 'white', border: '1.5px solid', borderColor: 'cream.line2', borderRadius: '10px',
  fontSize: '13px', color: 'ink.700', fontWeight: '600', size: 'sm',
  _focus: { borderColor: 'coral.500', boxShadow: '0 0 0 3px rgba(239,90,42,.1)' },
};

// ── main component ─────────────────────────────────────────────────────────────

const EvaluacionesDiferidas = () => {
  const toast   = useToast();
  const history = useHistory();

  // ── lista ──────────────────────────────────────────────────────────────────
  const [records, setRecords]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filterSearch, setFilter]   = useState('');
  const [filterAsig, setFilterAsig] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo]     = useState('');

  // ── inline edit ────────────────────────────────────────────────────────────
  const [editingId, setEditingId]   = useState(null);
  const [editData, setEditData]     = useState({});

  // ── panel nuevo registro ───────────────────────────────────────────────────
  const [panelOpen, setPanelOpen]   = useState(false);
  const [student, setStudent]       = useState(null);     // customer seleccionado
  const [stuQuery, setStuQuery]     = useState('');
  const [stuResults, setStuResults] = useState([]);
  const [stuLoading, setStuLoading] = useState(false);
  const [rows, setRows]             = useState([buildRow()]);
  const [saving, setSaving]         = useState(false);
  const debounceRef                 = useRef(null);

  // ── carga lista ────────────────────────────────────────────────────────────
  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getEvaluacionesPendientes({
        search:     filterSearch || undefined,
        asignatura: filterAsig   || undefined,
        fecha_from: filterFrom   || undefined,
        fecha_to:   filterTo     || undefined,
      });
      const payload = res?.data;
      // paginate() → { data: { data: [...], total, ... } }  |  get() → { data: [...] }
      setRecords(Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : []));
    } catch {
      toast({ title: 'Error al cargar registros', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setLoading(false);
    }
  }, [filterSearch, filterAsig, filterFrom, filterTo, toast]);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  // ── búsqueda de alumno (debounced) ─────────────────────────────────────────
  const searchStudent = useCallback((q) => {
    setStuQuery(q);
    setStudent(null);
    clearTimeout(debounceRef.current);
    if (q.length < 2) { setStuResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setStuLoading(true);
      try {
        const results = await searchCustomers(q, 8);
        setStuResults(results);
      } catch {
        setStuResults([]);
      } finally {
        setStuLoading(false);
      }
    }, 320);
  }, []);

  const selectStudent = (c) => {
    setStudent(c);
    setStuQuery(customerName(c));
    setStuResults([]);
  };

  // ── manejo de filas ────────────────────────────────────────────────────────
  const updateRow = (idx, field, val) =>
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: val } : r));

  const removeRow = (idx) =>
    setRows(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);

  const resetPanel = () => {
    setStudent(null); setStuQuery(''); setStuResults([]);
    setRows([buildRow()]); setPanelOpen(false);
  };

  // ── guardar registros ──────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!student) {
      toast({ title: 'Selecciona un alumno', status: 'warning', duration: 2500, isClosable: true });
      return;
    }
    const validRows = rows.filter(r => r.asignatura && r.nombre_evaluacion && r.fecha_realizada);
    if (!validRows.length) {
      toast({ title: 'Completa al menos una evaluación', status: 'warning', duration: 2500, isClosable: true });
      return;
    }
    setSaving(true);
    try {
      const payload = validRows.map(r => ({
        alumno_rut:        student.username,
        alumno_nombre:     customerName(student),
        alumno_curso:      customerCourse(student),
        asignatura:        r.asignatura,
        nombre_evaluacion: r.nombre_evaluacion,
        fecha_oficial:     r.fecha_oficial || null,
        fecha_realizada:   r.fecha_realizada,
      }));
      await createEvaluacionesPendientes(payload);
      toast({ title: `${validRows.length} evaluación(es) registrada(s)`, status: 'success', duration: 3000, isClosable: true });
      resetPanel();
      loadRecords();
    } catch (e) {
      toast({ title: 'Error al guardar', description: e?.response?.data?.message || 'Intenta nuevamente', status: 'error', duration: 4000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  // ── edición inline ─────────────────────────────────────────────────────────
  const startEdit = (r) => {
    setEditingId(r.id);
    setEditData({
      asignatura:        r.asignatura,
      nombre_evaluacion: r.nombre_evaluacion,
      fecha_oficial:     r.fecha_oficial || '',
      fecha_realizada:   r.fecha_realizada,
    });
  };

  const saveEdit = async () => {
    try {
      await updateEvaluacionPendiente(editingId, editData);
      setEditingId(null);
      loadRecords();
      toast({ title: 'Registro actualizado', status: 'success', duration: 2000, isClosable: true });
    } catch {
      toast({ title: 'Error al actualizar', status: 'error', duration: 3000, isClosable: true });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este registro?')) return;
    try {
      await deleteEvaluacionPendiente(id);
      loadRecords();
      toast({ title: 'Registro eliminado', status: 'info', duration: 2000, isClosable: true });
    } catch {
      toast({ title: 'Error al eliminar', status: 'error', duration: 3000, isClosable: true });
    }
  };

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    <Box bg="cream.bg" minH="100vh">

      {/* breadcrumb */}
      <Flex h="52px" align="center" gap="8px" px="28px" bg="white"
        borderBottom="1px solid" borderColor="cream.line">
        <Text fontSize="13.5px" fontWeight="600" color="ink.400" cursor="pointer"
          _hover={{ color: 'coral.500' }} onClick={() => history.push('/admin/dashboard')}>
          Inicio
        </Text>
        <Text color="cream.line2">/</Text>
        <Text fontSize="13.5px" fontWeight="700" color="ink.900">Evaluaciones Pendientes</Text>

        <Flex ml="auto" align="center" gap="8px">
          <Button size="sm" variant="ghost" color="ink.400" borderRadius="9px"
            leftIcon={<Icon as={FiRefreshCw} boxSize="13px" />}
            isLoading={loading} onClick={loadRecords} fontWeight="700" fontSize="13px">
            Actualizar
          </Button>
          <Button size="sm" colorScheme="orange" borderRadius="9px" fontWeight="700"
            leftIcon={panelOpen ? <ChevronUpIcon /> : <Icon as={FiPlus} boxSize="14px" />}
            onClick={() => setPanelOpen(v => !v)}>
            {panelOpen ? 'Cerrar' : 'Nuevo Registro'}
          </Button>
        </Flex>
      </Flex>

      <Box px="28px" py="22px">

        {/* ── Panel Nuevo Registro ─────────────────────────────────────────── */}
        <Collapse in={panelOpen} animateOpacity>
          <Box bg="white" border="1.5px solid" borderColor="coral.200"
            borderRadius="16px" p="22px" mb="22px"
            boxShadow="0 2px 12px rgba(239,90,42,.08)">

            <Text fontSize="13px" fontWeight="800" color="coral.600"
              textTransform="uppercase" letterSpacing=".07em" mb="16px">
              Nuevo registro de evaluación pendiente
            </Text>

            {/* Búsqueda de alumno */}
            <Box mb="18px" maxW="440px" position="relative">
              <Text fontSize="12px" fontWeight="700" color="ink.500" mb="6px">
                Alumno
              </Text>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <Icon as={FiSearch} color="ink.400" boxSize="13px" />
                </InputLeftElement>
                <Input
                  value={stuQuery}
                  onChange={e => searchStudent(e.target.value)}
                  placeholder="Buscar por nombre o RUT…"
                  {...inputSt}
                  pl="32px"
                />
                {stuLoading && (
                  <InputRightElement>
                    <Spinner size="xs" color="coral.500" />
                  </InputRightElement>
                )}
              </InputGroup>

              {/* Dropdown resultados */}
              {stuResults.length > 0 && (
                <Box position="absolute" top="100%" left="0" right="0" zIndex="200"
                  bg="white" border="1.5px solid" borderColor="cream.line2"
                  borderRadius="12px" boxShadow="0 8px 24px rgba(0,0,0,.12)"
                  mt="4px" maxH="240px" overflowY="auto">
                  {stuResults.map(c => (
                    <Flex key={c.id} align="center" gap="10px" px="14px" py="10px"
                      cursor="pointer" _hover={{ bg: 'cream.soft' }}
                      onClick={() => selectStudent(c)}>
                      <Avatar name={customerName(c)} size={28} />
                      <Box>
                        <Text fontSize="13px" fontWeight="700" color="ink.900">
                          {customerName(c)}
                        </Text>
                        <Text fontSize="11px" color="ink.400" fontWeight="600">
                          RUT: {c.username}  ·  {customerCourse(c) || 'Sin curso'}
                        </Text>
                      </Box>
                    </Flex>
                  ))}
                </Box>
              )}
            </Box>

            {/* Card alumno seleccionado */}
            {student && (
              <Flex align="center" gap="12px" bg="cream.soft" borderRadius="12px"
                px="14px" py="10px" mb="18px" maxW="440px">
                <Avatar name={customerName(student)} size={36} />
                <Box flex="1">
                  <Text fontSize="13.5px" fontWeight="800" color="ink.900">
                    {customerName(student)}
                  </Text>
                  <Text fontSize="11.5px" color="ink.400" fontWeight="600">
                    {student.username}  ·  {customerCourse(student) || 'Sin curso'}
                  </Text>
                </Box>
                <IconButton size="xs" variant="ghost" color="ink.400" borderRadius="8px"
                  icon={<CloseIcon boxSize="9px" />} aria-label="Quitar alumno"
                  onClick={() => { setStudent(null); setStuQuery(''); }} />
              </Flex>
            )}

            {/* Filas de evaluaciones */}
            <Box mb="14px">
              <Flex gap="8px" mb="8px" display={{ base: 'none', md: 'flex' }}>
                {['Asignatura', 'Nombre de la evaluación', 'Fecha oficial (opcional)', 'Fecha realizada *'].map(h => (
                  <Text key={h} fontSize="11px" fontWeight="700" color="ink.400"
                    textTransform="uppercase" letterSpacing=".06em"
                    flex={h.includes('Nombre') ? '2' : '1'} minW="0">
                    {h}
                  </Text>
                ))}
                <Box w="28px" flexShrink={0} />
              </Flex>

              {rows.map((row, idx) => (
                <Flex key={idx} gap="8px" mb="8px" align="center" flexWrap={{ base: 'wrap', md: 'nowrap' }}>
                  {/* Asignatura */}
                  <Select flex="1" minW="130px" value={row.asignatura}
                    onChange={e => updateRow(idx, 'asignatura', e.target.value)}
                    {...selectSt} placeholder="Asignatura">
                    {ASIGNATURAS.map(a => <option key={a} value={a}>{a}</option>)}
                  </Select>

                  {/* Nombre evaluación */}
                  <Input flex="2" minW="160px" value={row.nombre_evaluacion}
                    onChange={e => updateRow(idx, 'nombre_evaluacion', e.target.value)}
                    placeholder="Ej: Prueba unidad 3" {...inputSt} />

                  {/* Fecha oficial */}
                  <Input flex="1" minW="130px" type="date" value={row.fecha_oficial}
                    onChange={e => updateRow(idx, 'fecha_oficial', e.target.value)}
                    {...inputSt} />

                  {/* Fecha realizada */}
                  <Input flex="1" minW="130px" type="date" value={row.fecha_realizada}
                    onChange={e => updateRow(idx, 'fecha_realizada', e.target.value)}
                    {...inputSt} />

                  <IconButton size="sm" variant="ghost" color="ink.300" borderRadius="8px"
                    icon={<Icon as={FiX} boxSize="13px" />} aria-label="Quitar fila"
                    onClick={() => removeRow(idx)} isDisabled={rows.length === 1} />
                </Flex>
              ))}

              <Button size="xs" variant="ghost" color="coral.500" fontWeight="700"
                leftIcon={<AddIcon boxSize="9px" />}
                onClick={() => setRows(prev => [...prev, buildRow()])}>
                Agregar otra evaluación
              </Button>
            </Box>

            <Flex gap="10px">
              <Button size="sm" colorScheme="orange" borderRadius="9px" fontWeight="700"
                isLoading={saving} onClick={handleSave}
                isDisabled={!student || rows.every(r => !r.fecha_realizada)}>
                Guardar {rows.filter(r => r.fecha_realizada).length > 1
                  ? `${rows.filter(r => r.fecha_realizada).length} evaluaciones`
                  : 'evaluación'}
              </Button>
              <Button size="sm" variant="ghost" color="ink.400" borderRadius="9px"
                fontWeight="700" onClick={resetPanel}>
                Cancelar
              </Button>
            </Flex>
          </Box>
        </Collapse>

        {/* ── Filtros ──────────────────────────────────────────────────────── */}
        <Flex align="center" gap="10px" mb="16px" flexWrap="wrap">
          <InputGroup maxW="260px" size="sm">
            <InputLeftElement pointerEvents="none">
              <Icon as={FiSearch} color="ink.400" boxSize="13px" />
            </InputLeftElement>
            <Input placeholder="Alumno o RUT…" value={filterSearch}
              onChange={e => setFilter(e.target.value)}
              bg="white" border="1.5px solid" borderColor="cream.line2" borderRadius="11px"
              fontSize="13px" pl="32px"
              _focus={{ borderColor: 'coral.500', boxShadow: '0 0 0 3px rgba(239,90,42,.1)' }} />
          </InputGroup>

          <Select value={filterAsig} onChange={e => setFilterAsig(e.target.value)}
            maxW="200px" placeholder="Todas las asignaturas" {...selectSt} size="sm">
            {ASIGNATURAS.map(a => <option key={a} value={a}>{a}</option>)}
          </Select>

          <Input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)}
            maxW="150px" placeholder="Desde" {...inputSt} />
          <Input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)}
            maxW="150px" placeholder="Hasta" {...inputSt} />

          {(filterSearch || filterAsig || filterFrom || filterTo) && (
            <Button size="sm" variant="ghost" color="ink.400" borderRadius="9px"
              leftIcon={<Icon as={FiX} boxSize="12px" />} fontWeight="700"
              onClick={() => { setFilter(''); setFilterAsig(''); setFilterFrom(''); setFilterTo(''); }}>
              Limpiar
            </Button>
          )}

          <Text fontSize="12px" color="ink.400" fontWeight="700" ml="auto">
            {records.length} registro{records.length !== 1 ? 's' : ''}
          </Text>
        </Flex>

        {/* ── Tabla ────────────────────────────────────────────────────────── */}
        <Box bg="white" border="1px solid" borderColor="cream.line"
          borderRadius="16px" overflow="hidden" boxShadow="0 1px 3px rgba(54,38,22,.06)">
          {loading ? (
            <Flex align="center" justify="center" h="200px">
              <Spinner size="xl" color="coral.500" thickness="3px" />
            </Flex>
          ) : records.length === 0 ? (
            <Flex align="center" justify="center" h="200px" direction="column" gap="10px">
              <Icon as={FiUser} boxSize="36px" color="ink.300" />
              <Text color="ink.400" fontWeight="600" fontSize="14px">
                No hay registros
              </Text>
              <Button size="sm" colorScheme="orange" borderRadius="9px" fontWeight="700"
                onClick={() => setPanelOpen(true)}>
                Agregar el primero
              </Button>
            </Flex>
          ) : (
            <Box overflowX="auto">
              <Table size="sm" variant="unstyled">
                <Thead>
                  <Tr borderBottom="2px solid" borderColor="cream.line">
                    {['Alumno', 'RUT', 'Curso', 'Asignatura', 'Evaluación', 'Fecha oficial', 'Fecha realizada', ''].map(h => (
                      <Th key={h} py="12px" px="16px" fontSize="11px" fontWeight="800"
                        color="ink.400" textTransform="uppercase" letterSpacing=".07em"
                        whiteSpace="nowrap">
                        {h}
                      </Th>
                    ))}
                  </Tr>
                </Thead>
                <Tbody>
                  {records.map((r, i) => (
                    <Tr key={r.id}
                      bg={i % 2 === 0 ? 'white' : 'cream.bg'}
                      borderBottom="1px solid" borderColor="cream.line"
                      _hover={{ bg: 'cream.soft' }}
                      transition="background .1s">

                      {/* Alumno */}
                      <Td px="16px" py="10px" minW="160px">
                        <Flex align="center" gap="8px"
                          cursor="pointer" role="group"
                          onClick={() => history.push(`/admin/usuario/${r.alumno_rut}`)}>
                          <Avatar name={r.alumno_nombre || ''} size={28} />
                          <Text fontSize="13px" fontWeight="700" color="ink.900"
                            whiteSpace="nowrap"
                            _groupHover={{ color: 'coral.500', textDecoration: 'underline' }}>
                            {r.alumno_nombre || '—'}
                          </Text>
                        </Flex>
                      </Td>

                      {/* RUT */}
                      <Td px="16px" py="10px">
                        <Text fontSize="12.5px" color="ink.500" fontWeight="600"
                          fontFamily="mono">
                          {r.alumno_rut || '—'}
                        </Text>
                      </Td>

                      {/* Curso */}
                      <Td px="16px" py="10px">
                        {r.alumno_curso ? (
                          <Badge colorScheme="blue" borderRadius="6px" fontSize="11px"
                            fontWeight="700" px="8px" py="2px">
                            {r.alumno_curso}
                          </Badge>
                        ) : <Text color="ink.300">—</Text>}
                      </Td>

                      {/* Asignatura — editable */}
                      <Td px="16px" py="10px" minW="150px">
                        {editingId === r.id ? (
                          <Select size="xs" value={editData.asignatura}
                            onChange={e => setEditData(d => ({ ...d, asignatura: e.target.value }))}
                            {...selectSt} borderRadius="8px">
                            {ASIGNATURAS.map(a => <option key={a} value={a}>{a}</option>)}
                          </Select>
                        ) : (
                          <Text fontSize="13px" color="ink.700" fontWeight="600">
                            {r.asignatura}
                          </Text>
                        )}
                      </Td>

                      {/* Nombre evaluación — editable */}
                      <Td px="16px" py="10px" minW="180px">
                        {editingId === r.id ? (
                          <Input size="xs" value={editData.nombre_evaluacion}
                            onChange={e => setEditData(d => ({ ...d, nombre_evaluacion: e.target.value }))}
                            {...inputSt} borderRadius="8px" />
                        ) : (
                          <Text fontSize="13px" color="ink.700" fontWeight="600">
                            {r.nombre_evaluacion}
                          </Text>
                        )}
                      </Td>

                      {/* Fecha oficial — editable */}
                      <Td px="16px" py="10px" minW="130px">
                        {editingId === r.id ? (
                          <Input type="date" size="xs" value={editData.fecha_oficial}
                            onChange={e => setEditData(d => ({ ...d, fecha_oficial: e.target.value }))}
                            {...inputSt} borderRadius="8px" />
                        ) : (
                          <Text fontSize="12.5px" color="ink.400" fontWeight="600">
                            {fmtDate(r.fecha_oficial)}
                          </Text>
                        )}
                      </Td>

                      {/* Fecha realizada — editable */}
                      <Td px="16px" py="10px" minW="130px">
                        {editingId === r.id ? (
                          <Input type="date" size="xs" value={editData.fecha_realizada}
                            onChange={e => setEditData(d => ({ ...d, fecha_realizada: e.target.value }))}
                            {...inputSt} borderRadius="8px" />
                        ) : (
                          <Text fontSize="12.5px" color="coral.600" fontWeight="700">
                            {fmtDate(r.fecha_realizada)}
                          </Text>
                        )}
                      </Td>

                      {/* Acciones */}
                      <Td px="12px" py="10px">
                        <Flex gap="4px" justify="flex-end">
                          {editingId === r.id ? (
                            <>
                              <IconButton size="xs" colorScheme="green" borderRadius="7px"
                                icon={<Icon as={FiCheck} boxSize="12px" />}
                                aria-label="Guardar" onClick={saveEdit} />
                              <IconButton size="xs" variant="ghost" color="ink.400"
                                borderRadius="7px"
                                icon={<Icon as={FiX} boxSize="12px" />}
                                aria-label="Cancelar" onClick={() => setEditingId(null)} />
                            </>
                          ) : (
                            <>
                              <IconButton size="xs" variant="ghost" color="ink.400"
                                borderRadius="7px"
                                icon={<Icon as={FiEdit2} boxSize="12px" />}
                                aria-label="Editar" onClick={() => startEdit(r)} />
                              <IconButton size="xs" variant="ghost" color="red.400"
                                borderRadius="7px"
                                icon={<Icon as={FiTrash2} boxSize="12px" />}
                                aria-label="Eliminar" onClick={() => handleDelete(r.id)} />
                            </>
                          )}
                        </Flex>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default EvaluacionesDiferidas;
